import React, { FC, useState } from 'react';
import styled from 'styled-components';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { Order } from '../../../common/model/dto/order/order';
import { OrderItem } from '../../../common/model/dto/order/order-item';
import { OrderDataUtils } from '../../../common/utils/services/order-data-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvConfirmationDialog from '../molecules/OvConfirmationDialog';
import OvRecordDetailsSection from '../molecules/OvRecordDetailsSection';
import OvOrderItemFormDialog from '../molecules/OvOrderItemFormDialog';
import OvOrderItemsTable from '../molecules/OvOrderItemsTable';

interface OvOrderItemsSectionProps {
  order: Order;
  isUpdateAllowed: boolean;
  onItemsChange: (items: OrderItem[]) => void;
}

type ItemDialog =
  | { kind: 'closed' }
  | { kind: 'add' }
  | { kind: 'edit'; index: number }
  | { kind: 'remove'; index: number };

const OvOrderItemsSection: FC<OvOrderItemsSectionProps> = ({
  order,
  isUpdateAllowed,
  onItemsChange,
}) => {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<ItemDialog>({ kind: 'closed' });
  const isEditable = isUpdateAllowed && !order.is_submitted;
  const editedItem =
    dialog.kind === 'edit' ? order.items[dialog.index] : undefined;

  const closeDialog = () => setDialog({ kind: 'closed' });

  const onSave = (item: OrderItem) => {
    const items =
      dialog.kind === 'edit'
        ? order.items.map((existingItem, index) =>
            index === dialog.index ? item : existingItem
          )
        : [...order.items, item];

    closeDialog();
    onItemsChange(items);
  };

  const onRemove = () => {
    if (dialog.kind === 'remove') {
      closeDialog();
      onItemsChange(order.items.filter((_, index) => index !== dialog.index));
    }
  };

  return (
    <OvRecordDetailsSection
      title={
        <>
          {t('orders.details.products')}{' '}
          <ItemCount>
            {t('orders.itemCount', {
              count: OrderDataUtils.getItemCount(order),
            })}
          </ItemCount>
        </>
      }
      action={
        isEditable ? (
          <AddButton type="button" onClick={() => setDialog({ kind: 'add' })}>
            <AddIcon aria-hidden="true" />
            {t('orders.details.item.add')}
          </AddButton>
        ) : (
          <ReadOnlyReason>
            {order.is_submitted
              ? t('orders.details.item.submittedReadOnly')
              : t('orders.details.item.readOnlyAccess')}
          </ReadOnlyReason>
        )
      }
    >
      <OvOrderItemsTable
        items={order.items}
        isEditable={isEditable}
        onEdit={(index) => setDialog({ kind: 'edit', index })}
        onRemove={(index) => setDialog({ kind: 'remove', index })}
      />
      <Totals>
        <div>
          <dt>{t('orders.details.subtotal')}</dt>
          <dd>
            {OrderDataUtils.formatAmount(OrderDataUtils.getItemsTotal(order))}
          </dd>
        </div>
        <div>
          <dt>{t('orders.details.shipping')}</dt>
          <dd>
            {OrderDataUtils.formatAmount(order.shipping_rate_amount ?? 0)}
          </dd>
        </div>
        <div>
          <dt>{t('orders.generalInfo.total')}</dt>
          <GrandTotal>
            {OrderDataUtils.formatAmount(OrderDataUtils.getTotal(order))}
          </GrandTotal>
        </div>
      </Totals>

      {isEditable && (
        <>
          <OvConfirmationDialog
            isOpen={dialog.kind === 'remove'}
            onCancel={closeDialog}
            onConfirm={onRemove}
            description="orders.details.permanentItemRemove"
            title="common.areYouSure"
          />
          <OvOrderItemFormDialog
            isOpen={dialog.kind === 'add' || dialog.kind === 'edit'}
            onCancel={closeDialog}
            onSave={onSave}
            title={
              editedItem
                ? t('orders.details.editOrderItem', { name: editedItem.sku })
                : t('orders.details.createItem')
            }
            orderItem={editedItem}
          />
        </>
      )}
    </OvRecordDetailsSection>
  );
};

export default OvOrderItemsSection;

const ItemCount = styled.span`
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
  font-weight: normal;
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  min-height: 1.875rem;
  padding: 0.3125rem 0.5625rem;
  border: 1px solid transparent;
  border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
  background: transparent;
  color: ${Colours.OV_BASE};
  font: inherit;
  font-size: ${Variables.fontSizes.MEDIUM};
  cursor: pointer;

  &:hover {
    background-color: ${Colours.OV_WHITE_HOVER};
  }

  &:focus-visible {
    outline: 3px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: 2px;
  }

  .MuiSvgIcon-root {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const ReadOnlyReason = styled.span`
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;

const Totals = styled.dl`
  display: flex;
  justify-content: flex-end;
  gap: 1.5rem;
  margin: 0.875rem 0 0;
  padding-top: 0.75rem;
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  text-align: right;

  dt {
    margin-bottom: 0.3125rem;
    color: ${Colours.OV_CHARCOAL};
    font-size: ${Variables.fontSizes.SMALL};
  }

  dd {
    margin: 0;
    font-variant-numeric: tabular-nums;
  }
`;

const GrandTotal = styled.dd`
  font-size: ${Variables.fontSizes.LARGE};
  font-weight: bold;
`;
